<script lang="ts">
import { defineBlock } from "@bagelink/blox"
import { $, defineSchema } from "@bagelink/vue"
const schema = defineSchema({
  anchor: $.text("Section anchor", { default: "People" }),
  title: $.text("Heading", { default: "Featuring" }),
  specialHeading: $.checkbox("Use special heading font", { default: true }),
  intro: $.text("Introduction", { multiline: true }),
  size: $.select(
    "Card size",
    [
      { label: "Small", value: "small" },
      { label: "Medium", value: "medium" },
      { label: "Large", value: "large" },
      { label: "Extra large", value: "extra-large" },
    ],
    { default: "medium" },
  ),
  surface: $.select(
    "Background",
    [
      { label: "Page background", value: "page" },
      { label: "White surface", value: "surface" },
      { label: "Brand color", value: "brand" },
      { label: "Dark", value: "dark" },
      { label: "Image", value: "image" },
    ],
    { default: "page" },
  ),
  backgroundImage: $.upload("Background image").if('surface eq "image"'),
  backgroundFit: $.select(
    "Background image fit",
    [
      { label: "Cover", value: "cover" },
      { label: "Contain", value: "contain" },
      { label: "Fill", value: "fill" },
    ],
    { default: "cover" },
  ).if('surface eq "image" and backgroundImage pr'),
  dimBackground: $.checkbox("Dim background", { default: false }).if(
    'surface eq "image" and backgroundImage pr',
  ),
  imageShape: $.select(
    "Portrait shape",
    [
      { label: "Circle", value: "circle" },
      { label: "Portrait", value: "portrait" },
      { label: "Landscape", value: "landscape" },
    ],
    { default: "circle" },
  ),
  bioDisplay: $.select(
    "Biography display",
    [
      { label: "Dialog", value: "dialog" },
      { label: "Visible", value: "visible" },
      { label: "Hidden", value: "hidden" },
    ],
    { default: "dialog" },
  ),
  showBiographyButton: $.checkbox("Show Read biography button", {
    default: true,
  }).if('bioDisplay eq "dialog"'),
  showLearnMoreButton: $.checkbox("Show Learn more button", {
    default: true,
  }),
  people: $.array(
    "People",
    defineSchema({
      name: $.text("Name"),
      title: $.text("Role, award, or description"),
      image: $.upload("Portrait"),
      bio: $.richtext("Biography", { simple: true }),
      link: $.url("Optional link"),
    }),
    {
      collapsible: true,
      allowAdd: true,
      allowDelete: true,
      allowReorder: true,
      default: [],
    },
  ),
});
export const EventPeople = defineBlock({
  name: "EventPeople",
  label: "People",
  icon: "groups",
  thumbnail: "/block-thumbs/EventFeaturing.svg",
  category: "Event",
  order: 30,
  schema,
})
</script>
<script setup lang="ts">
import { computed, ref } from "vue";

import { Btn, Dialog, Icon, pathKeyToURL, sanitizeHtml } from "@bagelink/vue"

type Person = {
  name: string;
  title?: string;
  image?: string;
  bio?: string;
  link?: string;
};


;

const props = withDefaults(
  defineProps<{
    anchor?: string;
    title?: string;
    specialHeading?: boolean;
    intro?: string;
    size?: "small" | "medium" | "large" | "extra-large";
    surface?: "page" | "surface" | "brand" | "dark" | "image";
    backgroundImage?: string;
    backgroundFit?: "cover" | "contain" | "fill";
    dimBackground?: boolean;
    imageShape?: "circle" | "portrait" | "landscape";
    bioDisplay?: "dialog" | "visible" | "hidden";
    showBiographyButton?: boolean;
    showLearnMoreButton?: boolean;
    people?: Person[];
  }>(),
  {
    anchor: "People",
    title: "Featuring",
    specialHeading: true,
    intro: "",
    size: "medium",
    surface: "page",
    backgroundImage: "",
    backgroundFit: "cover",
    dimBackground: false,
    imageShape: "circle",
    bioDisplay: "dialog",
    showBiographyButton: true,
    showLearnMoreButton: true,
    people: () => [],
  },
);

const selected = ref<Person | null>(null);
const open = ref(false);
const sectionClass = computed(
  () =>
    ({
      page: "bg-transparent",
      surface: "bg-box",
      brand: "bg-primary color-white keep-white",
      dark: "bg-black color-white keep-white",
      image: "",
    })[props.surface],
);
const sectionStyle = computed(() =>
  props.surface === "image" && props.backgroundImage
    ? {
        backgroundImage: `${props.dimBackground ? "linear-gradient(#0006, #0006), " : ""}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
        backgroundSize:
          props.backgroundFit === "fill"
            ? "100% 100%"
            : props.backgroundFit,
      }
    : undefined,
);
const hasPeople = computed(() =>
  props.people.some((person) => person.name?.trim() || person.image),
);
const displayedPeople = computed<Person[]>(() =>
  props.people.filter((person) => person.name?.trim() || person.image),
);
const cardSizeClass = computed(
  () =>
    ({
      small: "w-100p w-150px txt12",
      medium: "w-100p w-200px txt14",
      large: "w-100p w-250px ms-0 me-0 txt16",
      "extra-large": "w-100p w-300px ms-0 me-0 txt-18",
    })[props.size],
);
const imageClass = computed(
  () =>
    ({
      circle: "round aspect-ratio-1",
      portrait: "radius-2 aspect-ratio-3-4",
      landscape: "radius-2 aspect-ratio-4-3",
    })[props.imageShape],
);

const personNameClass = computed(
  () =>
    ({
      small: "txt-18 line-height-13",
      medium: "txt-22 line-height-13",
      large: "txt-28 m_txt-24 line-height-12",
      "extra-large": "txt-36 m_txt-28 line-height-11",
    })[props.size],
);

function showBio(person: Person) {
  if (!hasPeople.value || props.bioDisplay !== "dialog" || !person.bio) return;
  selected.value = person;
  open.value = true;
}
</script>

<template>
  <section
    :id="anchor"
    class="blox-background-image px-2 py-5 m_px-1 m_py-2"
    :class="sectionClass"
    :style="sectionStyle"
  >
    <div class="w-1100px">
      <h2
        v-if="title"
        class="m-0 txt-70 m_txt-40 line-height-1 m_line-height-11 balance txt-center pb-1"
        :class="{ 'blox-special-heading': specialHeading }"
      >
        {{ title }}
      </h2>
      <p v-if="intro" class="txt-center pretty w-700px pb-2">{{ intro }}</p>
      <div
        v-if="hasPeople"
        class="display-flex align-items-start justify-content-center flex-wrap gap-2 m_gap-1"
      >
        <div
          v-for="(person, index) in displayedPeople"
          :key="`${person.name}-${index}`"
          class="txt-center flex-shrink-0"
          :class="cardSizeClass"
        >
          <img
            v-if="person.image"
            :src="pathKeyToURL(person.image) || person.image"
            :alt="person.name"
            class="w-100p cover block max-h-500px"
            :class="imageClass"
            loading="lazy"
          />
          <div
            v-else
            class="w-100p bg-bg border flex-center"
            :class="imageClass"
          >
            <Icon name="person" :size="4" class="color-gray" />
          </div>
          <p v-if="person.title" class="m-0 pt-075">{{ person.title }}</p>
          <h3 v-if="person.name" class="m-0 balance pt-05" :class="personNameClass">
            {{ person.name }}
          </h3>
          <!-- eslint-disable vue/no-v-html -- Rich text is sanitized before rendering. -->
          <div
            v-if="bioDisplay === 'visible' && person.bio"
            class="pretty pt-075 txt-start"
            v-html="sanitizeHtml(person.bio)"
          />
          <!-- eslint-enable vue/no-v-html -->
          <Btn
            v-if="
              bioDisplay === 'dialog' &&
              showBiographyButton &&
              person.bio &&
              hasPeople
            "
            flat
            icon-end="open_in_new"
            value="Read biography"
            @click="showBio(person)"
          />
          <Btn
            v-if="showLearnMoreButton && person.link"
            flat
            icon-end="arrow_forward"
            value="Learn more"
            :href="person.link"
          />
        </div>
      </div>
    </div>
    <Dialog v-model:open="open" :title="selected?.name" width="m">
      <!-- eslint-disable vue/no-v-html -- Rich text is sanitized before rendering. -->
      <div
        v-if="selected?.bio"
        class="pretty"
        v-html="sanitizeHtml(selected.bio)"
      />
      <!-- eslint-enable vue/no-v-html -->
    </Dialog>
  </section>
</template>
